import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismModal } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import GeneralCountryFlag from 'lumen/components/general/country-flag';
import GeneralTooltipsTooltipEvents from 'lumen/components/general/tooltips/tooltip-events';
import type { DayEvent } from 'lumen/components/hotel/events/calendars/calendar-events';
import HotelEventsCalendarsDaysContentEventBar from 'lumen/components/hotel/events/calendars/days/content/event-bar';
import type { Day, Holiday } from 'lumen/models';
import highlightElement from 'lumen/modifiers/highlight-element';

interface HotelEventsCalendarsDaysDayEventsSignature {
  Args: {
    addEvent: (startDate: string) => void;
    column: number;
    eventsForDay: DayEvent[];
    isLoading: boolean;
    maxVisibleEvents: number;
    model: Day;
    monthMoment: Dayjs;
    refreshEvents: () => void;
  };
}

export default class HotelEventsCalendarsDaysDayEvents extends Component<HotelEventsCalendarsDaysDayEventsSignature> {
  @service declare intl: Services['intl'];

  @tracked isRightColumnHovered = false;
  @tracked isShowingModal = false;

  rightColumnMouseEnterTimeout: NodeJS.Timeout | null = null;

  get day(): Day {
    return this.args.model;
  }

  get dayId(): string {
    return `smart-insight-day-${this.day.date.format('YYYY-MM-DD')}`;
  }

  get dayNumber(): number | null {
    if (this.day?.date) {
      return this.day.date.date();
    }
    return null;
  }

  /** CSS Grid placement: day cell spans all rows in its column */
  get gridStyle(): ReturnType<typeof htmlSafe> {
    return htmlSafe(`grid-column: ${this.args.column}; grid-row: 1 / -1;`);
  }

  get hasMoreButton(): boolean {
    if (!this.args.eventsForDay.length) {
      return false;
    }

    return this.moreCount > 0;
  }

  get holidays(): Holiday[] {
    return [...(this.day?.holidays ?? [])];
  }

  get isPartOfInactiveMonth(): boolean {
    if (this.day?.date) {
      return !this.day.date.isSame(this.args.monthMoment, 'month');
    }
    return false;
  }

  get isToday(): boolean {
    if (this.day?.date) {
      return this.day.date.isSame(dayjs(), 'day');
    }
    return false;
  }

  get moreCount(): number {
    return this.args.eventsForDay.filter(
      (x) => x.order >= this.args.maxVisibleEvents,
    ).length;
  }

  get sortedEvents(): DayEvent[] {
    return this.args.eventsForDay.toSorted(
      (a, b) => a.event.statusOrder - b.event.statusOrder,
    );
  }

  get titleModalMoreEvents(): string {
    return `${this.intl.t('events.more_events_modal_title')} ${this.day?.date.format('LL')}`;
  }

  @action
  handleDoubleClick(e: MouseEvent): void {
    if (
      typeof (e.target as HTMLElement).className?.indexOf === 'function' &&
      (e.target as HTMLElement).className.indexOf('event-bar') !== -1
    ) {
      return;
    }

    this.args.addEvent(this.day?.date.format('YYYY-MM-DD'));
  }

  @action
  rightColumnMouseEnter(): void {
    this.rightColumnMouseEnterTimeout = setTimeout(() => {
      this.isRightColumnHovered = true;
    }, 50);
  }

  @action
  rightColumnMouseLeave(): void {
    if (this.rightColumnMouseEnterTimeout) {
      clearTimeout(this.rightColumnMouseEnterTimeout);
    }
    this.isRightColumnHovered = false;
  }

  @action
  toggleMoreEvents(): void {
    this.isShowingModal = !this.isShowingModal;
  }

  <template>
    {{! template-lint-disable no-invalid-interactive }}
    <div
      class="events-calendar__day"
      style={{this.gridStyle}}
      {{highlightElement id=this.dayId isLoading=@isLoading}}
      {{on "dblclick" this.handleDoubleClick}}
    >
      <div class="events-calendar__day-header">
        <div
          class="events-calendar__day-number
            {{if this.isToday 'events-calendar__day-number--today'}}
            {{if
              this.isPartOfInactiveMonth
              'events-calendar__day-number--inactive'
            }}"
        >
          {{this.dayNumber}}
        </div>
        <div
          class="events-calendar__day-holidays"
          role="tooltip"
          {{on "mouseenter" this.rightColumnMouseEnter}}
          {{on "mouseleave" this.rightColumnMouseLeave}}
        >
          {{#if this.holidays.length}}
            {{#each this.day.holidayCountries as |country|}}
              <GeneralCountryFlag @countryCode={{country}} />
            {{/each}}
            {{#if this.isRightColumnHovered}}
              <EmberTooltip
                @isShown={{this.isRightColumnHovered}}
                @side="left"
                @tooltipClass="base-tooltip day-detail-tooltip"
              >
                <GeneralTooltipsTooltipEvents @holidays={{this.holidays}} />
              </EmberTooltip>
            {{/if}}
          {{/if}}
        </div>
      </div>

      {{#if this.hasMoreButton}}
        <PrismButton
          @buttonType="ghost"
          @isSmall={{true}}
          @label={{t "insight_pages.breakdown.more" count=this.moreCount}}
          class="events-calendar__more-btn"
          {{on "click" this.toggleMoreEvents}}
        />
        {{#if this.isShowingModal}}
          <PrismModal
            @onClose={{this.toggleMoreEvents}}
            @title={{this.titleModalMoreEvents}}
          >
            <div class="more-events-modal-wrapper">
              {{#each this.sortedEvents as |event|}}
                <HotelEventsCalendarsDaysContentEventBar
                  @data={{event}}
                  @ignorePositioning={{true}}
                  @isLoading={{@isLoading}}
                  @refreshEvents={{@refreshEvents}}
                />
              {{/each}}
            </div>
          </PrismModal>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
